import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Loader2, Plus, X } from "lucide-react";

interface Category {
  Id: number;
  Title: string;
  "Product-items": number;
  Orders: number;
}

interface Product {
  Id: number;
  Title: string;
}

interface VeilLength {
  Id: number;
  Title: string;
}

interface Color {
  Id: number;
  Title: string;
}

interface OrderItem {
  categoryId: string;
  productId: string;
  veilLength: string;
  customSize?: string;
  color: string;
  quantity: number;
}

interface OrderFormData {
  brideName: string;
  specialRequest: string;
  weddingDate: string;
  requiredDate: string;
  deliverTo: "Shop" | "Bride";
  clientAddress: string;
  items: OrderItem[];
}

interface OrderFormProps {
  categories: Category[];
  veilLengths: VeilLength[];
  colors: Color[];
  onSubmit: (formData: OrderFormData) => void;
  onCancel: () => void;
  onCategoryChange: (index: number, categoryId: string) => void;
  getProductsForItem: (index: number) => Product[];
  isLoadingProductsForItem: (index: number) => boolean;
  formData: OrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>;
}

const OrderForm = ({
  categories,
  veilLengths,
  colors,
  onSubmit,
  onCancel,
  onCategoryChange,
  getProductsForItem,
  isLoadingProductsForItem,
  formData,
  setFormData,
}: OrderFormProps) => {
  const [orderItems, setOrderItems] = useState<OrderItem[]>(
    formData.items.length > 0
      ? formData.items
      : [
          {
            categoryId: "",
            productId: "",
            veilLength: "",
            customSize: "",
            color: "",
            quantity: 1,
          },
        ]
  );

  const handleChange = (
    field: keyof Omit<OrderFormData, "items">,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // If switching to Bride delivery, keep only one item
    if (field === "deliverTo" && value === "Bride" && orderItems.length > 1) {
      setOrderItems([orderItems[0]]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formattedItems = orderItems.map((item) => ({
      ...item,
      orderString:
        item.veilLength === "custom" && item.customSize
          ? `${item.quantity} | ${getProductTitle(item.productId)} | ${item.customSize} | ${getColorTitle(item.color)}`
          : `${item.quantity} | ${getProductTitle(item.productId)} | ${getVeilLengthTitle(item.veilLength)} | ${getColorTitle(item.color)}`,
    }));
    onSubmit({ ...formData, items: formattedItems });
  };

  const getProductTitle = (id: string) => {
    const products = getProductsForItem(0); // Using first index as products are same for all items
    return products?.find((p) => String(p.Id) === id)?.Title || id;
  };

  const getVeilLengthTitle = (id: string) => {
    if (id === "custom") return "Custom";
    return veilLengths?.find((v) => String(v.Id) === id)?.Title || id;
  };

  const getColorTitle = (id: string) => {
    return colors?.find((c) => String(c.Id) === id)?.Title || id;
  };

  const handleAddItem = () => {
    setOrderItems([
      ...orderItems,
      {
        categoryId: "",
        productId: "",
        veilLength: "",
        customSize: "",
        color: "",
        quantity: 1,
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (
    index: number,
    field: keyof OrderItem,
    value: string | number
  ) => {
    const newItems = [...orderItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
      // Clear custom size when changing veil length to non-custom
      ...(field === "veilLength" && value !== "custom" ? { customSize: "" } : {}),
    };
    setOrderItems(newItems);

    if (field === "categoryId") {
      onCategoryChange(index, String(value));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2 col-span-2">
          <label className="text-sm font-medium">Bride's Name</label>
          <Input
            type="text"
            value={formData.brideName}
            onChange={(e) => handleChange("brideName", e.target.value)}
            required
            placeholder="Enter bride's name"
          />
        </div>

        <div className="space-y-2 col-span-2">
          <label className="text-sm font-medium">Deliver To</label>
          <Select
            value={formData.deliverTo}
            onValueChange={(value) =>
              handleChange("deliverTo", value as "Shop" | "Bride")
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select delivery location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Shop">Shop Address</SelectItem>
              <SelectItem value="Bride">Bride Address</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.deliverTo === "Bride" && (
          <div className="space-y-2 col-span-2">
            <label className="text-sm font-medium">Delivery Address</label>
            <Textarea
              value={formData.clientAddress}
              onChange={(e) => handleChange("clientAddress", e.target.value)}
              required={formData.deliverTo === "Bride"}
              placeholder="Enter delivery address"
            />
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium">Required Date</label>
          <div className="relative">
            <DatePicker
              selected={formData.requiredDate ? new Date(formData.requiredDate) : null}
              onChange={(date: Date) => {
                // Format date as YYYY-MM-DD for consistency (internal storage format)
                const formattedDate = date ? date.toISOString().split("T")[0] : "";
                handleChange("requiredDate", formattedDate);
              }}
              minDate={new Date()}
              maxDate={formData.weddingDate ? new Date(formData.weddingDate) : undefined}
              dateFormat="dd-MM-yyyy"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholderText="Select required date"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Wedding Date</label>
          <div className="relative">
            <DatePicker
              selected={formData.weddingDate ? new Date(formData.weddingDate) : null}
              onChange={(date: Date) => {
                // Format date as YYYY-MM-DD for consistency (internal storage format)
                const formattedDate = date ? date.toISOString().split("T")[0] : "";
                handleChange("weddingDate", formattedDate);
                
                // If required date is after wedding date, reset it
                if (formData.requiredDate && formData.requiredDate > formattedDate) {
                  handleChange("requiredDate", "");
                }
              }}
              minDate={formData.requiredDate ? new Date(formData.requiredDate) : new Date()}
              dateFormat="dd-MM-yyyy"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholderText="Select wedding date"
              required
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Special Request</label>
        <Textarea
          value={formData.specialRequest}
          onChange={(e) => handleChange("specialRequest", e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Order Items</h3>
          {formData.deliverTo !== "Bride" && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddItem}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Item
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {orderItems.map((item, index) => (
            <div
              key={index}
              className="p-4 border rounded-lg bg-gray-50 space-y-4 relative"
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                onClick={() => handleRemoveItem(index)}
              >
                <X className="w-4 h-4" />
              </Button>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Select
                    value={item.categoryId}
                    onValueChange={(value) => {
                      handleItemChange(index, "categoryId", value);
                      onCategoryChange(index, value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.Id}
                          value={category.Id.toString()}
                        >
                          {category.Title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Product</label>
                  <Select
                    value={item.productId}
                    onValueChange={(value) =>
                      handleItemChange(index, "productId", value)
                    }
                    disabled={
                      !item.categoryId || isLoadingProductsForItem(index)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {getProductsForItem(index)?.map((product) => (
                        <SelectItem
                          key={product.Id}
                          value={product.Id.toString()}
                        >
                          {product.Title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Veil Length</label>
                  <Select
                    value={item.veilLength}
                    onValueChange={(value) =>
                      handleItemChange(index, "veilLength", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select veil length" />
                    </SelectTrigger>
                    <SelectContent>
                      {veilLengths.map((length) => (
                        <SelectItem
                          key={length.Id}
                          value={length.Id.toString()}
                        >
                          {length.Title}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {item.veilLength === "custom" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Custom Size</label>
                    <Input
                      type="text"
                      value={item.customSize || ""}
                      onChange={(e) =>
                        handleItemChange(index, "customSize", e.target.value)
                      }
                      placeholder="Enter custom size"
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Colour</label>
                  <Select
                    value={item.color}
                    onValueChange={(value) =>
                      handleItemChange(index, "color", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select colour" />
                    </SelectTrigger>
                    <SelectContent>
                      {colors.map((color) => (
                        <SelectItem key={color.Id} value={color.Id.toString()}>
                          {color.Title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Quantity</label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(
                        index,
                        "quantity",
                        parseInt(e.target.value)
                      )
                    }
                    required
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 bg-white pt-4 border-t flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Submit Order</Button>
      </div>
    </form>
  );
};

export default OrderForm;
